import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { earlySupporterApi } from '@/services/earlySupporter.api';
import { useAuth } from '@/hooks/useAuth';
import type { EsCheckoutStatus, EsEntitlementView, EsOffer, EsUpgradeQuote } from '@/types/earlySupporter';

/** Query keys for the ES feature (one place, so invalidation never drifts). */
export const esKeys = {
  offer: ['early-supporter', 'offer'] as const,
  entitlement: ['early-supporter', 'entitlement'] as const,
  upgradeQuote: (packageId: number, period: string) => ['early-supporter', 'upgrade-quote', packageId, period] as const,
  checkout: (id: string) => ['early-supporter', 'checkout', id] as const,
};

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 5 * 60 * 1000;
const TERMINAL: readonly EsCheckoutStatus[] = ['PAID', 'VOIDED', 'FAILED'];

export const useEsOffer = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<EsOffer>({
    queryKey: esKeys.offer,
    queryFn: earlySupporterApi.getOffer,
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: 1,
  });
};

export const useEsEntitlement = (enabled = true) => {
  const { isAuthenticated } = useAuth();
  return useQuery<EsEntitlementView | null>({
    queryKey: esKeys.entitlement,
    queryFn: earlySupporterApi.getEntitlement,
    enabled: enabled && isAuthenticated,
    staleTime: 30_000,
    retry: 1,
  });
};

/** Quote is a mutation so the caller decides when to fire it (debounced on country / coupon apply). */
export const useEsQuote = () =>
  useMutation({
    mutationFn: earlySupporterApi.quote,
  });

export const useCreateEsCheckout = () =>
  useMutation({
    mutationFn: earlySupporterApi.createCheckout,
  });

/**
 * Polls a checkout every 3 s until PAID / VOIDED / FAILED or 5 minutes pass.
 * `timedOut` flips to true after 5 minutes without a terminal state.
 */
export const useEsCheckout = (id: string | null) => {
  const startedAt = useRef<number>(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    startedAt.current = Date.now();
    setTimedOut(false);
  }, [id]);

  const query = useQuery({
    queryKey: esKeys.checkout(id ?? 'none'),
    queryFn: () => earlySupporterApi.getCheckout(id as string),
    enabled: !!id && !timedOut,
    retry: 1,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && (TERMINAL.includes(data.status) && (data.status !== 'PAID' || data.activated))) return false;
      if (Date.now() - startedAt.current > POLL_MAX_MS) return false;
      return POLL_INTERVAL_MS;
    },
  });

  const data = query.data;
  const settled = !!data && TERMINAL.includes(data.status) && (data.status !== 'PAID' || data.activated);

  useEffect(() => {
    if (!id || settled) return;
    const remaining = POLL_MAX_MS - (Date.now() - startedAt.current);
    const timer = window.setTimeout(() => setTimedOut(true), Math.max(remaining, 0));
    return () => window.clearTimeout(timer);
  }, [id, settled]);

  return { ...query, timedOut: timedOut && !settled };
};

const useInvalidateEs = () => {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: esKeys.offer }),
      qc.invalidateQueries({ queryKey: esKeys.entitlement }),
      qc.invalidateQueries({ queryKey: ['billing-overview'] }),
      qc.invalidateQueries({ queryKey: ['billing-invoices'] }),
    ]);
};

export const useCancelEs = () => {
  const invalidate = useInvalidateEs();
  return useMutation({ mutationFn: earlySupporterApi.cancel, onSuccess: invalidate });
};

export const useResumeEs = () => {
  const invalidate = useInvalidateEs();
  return useMutation({ mutationFn: earlySupporterApi.resume, onSuccess: invalidate });
};

export const useSwitchEsToYearly = () =>
  useMutation({ mutationFn: earlySupporterApi.switchToYearly });

/** Upgrade preview. Never auto-retried (a failed quote shows a message); cached briefly per plan + period. */
export const useEsUpgradeQuote = (packageId: number | null, period: 'MONTH' | 'YEAR', enabled: boolean) =>
  useQuery<EsUpgradeQuote>({
    queryKey: esKeys.upgradeQuote(packageId ?? 0, period),
    queryFn: () => earlySupporterApi.quoteUpgrade({ packageId: packageId as number, period }),
    enabled: enabled && packageId !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

export const useEsUpgrade = () => useMutation({ mutationFn: earlySupporterApi.upgrade });
